import { useState } from 'react'
import {
  Download,
  Eye,
  FileSpreadsheet,
  FileText,
  Image as ImageIcon,
  Loader2,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  downloadIdeaAttachment,
  getIdeaAttachmentPreviewPath,
} from '@/idea-board/hooks/useIdeaBoardData'

function fileIcon(name) {
  const lower = (name || '').toLowerCase()
  if (/\.(jpg|jpeg|png)$/.test(lower)) return ImageIcon
  if (/\.(xlsx|xls)$/.test(lower)) return FileSpreadsheet
  return FileText
}

function formatBytes(bytes) {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function AttachmentItem({ attachment, showDownload = true }) {
  const Icon = fileIcon(attachment.original_filename)
  const [downloading, setDownloading] = useState(false)
  const [downloadError, setDownloadError] = useState(null)

  const handlePreview = () => {
    window.open(getIdeaAttachmentPreviewPath(attachment.id), '_blank', 'noopener,noreferrer')
  }

  const handleDownload = async () => {
    setDownloadError(null)
    setDownloading(true)
    try {
      await downloadIdeaAttachment(attachment)
    } catch (err) {
      setDownloadError(err.message || 'Download failed')
    } finally {
      setDownloading(false)
    }
  }

  return (
    <div className="rounded-lg border border-border bg-white px-3 py-2.5">
      <div className="flex items-center gap-2 flex-wrap">
        <Icon className="w-4 h-4 text-muted-foreground shrink-0" />
        <div className="flex-1 min-w-[120px]">
          <p className="text-sm font-medium truncate">{attachment.original_filename}</p>
          <p className="text-xs text-muted-foreground">{formatBytes(attachment.file_size)}</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-8 gap-1.5"
            onClick={handlePreview}
          >
            <Eye className="w-3.5 h-3.5" />
            Preview
          </Button>
          {showDownload && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-8 gap-1.5"
              disabled={downloading}
              onClick={handleDownload}
            >
              {downloading ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <Download className="w-3.5 h-3.5" />
              )}
              Download
            </Button>
          )}
        </div>
      </div>
      {downloadError && <p className="text-xs text-destructive mt-1.5">{downloadError}</p>}
    </div>
  )
}

export default function IdeaAttachmentPanel({ attachments = [], showDownload = true }) {
  if (!attachments.length) return null

  return (
    <div className="space-y-2">
      <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        Attachments ({attachments.length})
      </h4>
      <div className="space-y-2">
        {attachments.map((attachment) => (
          <AttachmentItem
            key={attachment.id}
            attachment={attachment}
            showDownload={showDownload}
          />
        ))}
      </div>
    </div>
  )
}
